import { Redirect } from 'expo-router'
import { useEffect } from 'react'
import { Platform, Linking } from 'react-native'
import { useCardActivityFeedBottomSheetHeightStore } from '@/src/experiences/card-activity/components/feed/_highnote/useCardActivityFeedBottomSheetHeightStore'
import { useBottomTabBarHeight } from '@react-navigation/bottom-tabs'
import { useEvent } from 'tamagui'
import { CardManagementModule } from '@/src/common/modules/CardManagementModule'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { useLogError } from '@/src/common/hooks/useLogError'
import { routerBack } from '@/src/common/utils/routerBack'
import { CardAccountSummary } from '@/src/experiences/card-accounts/components/CardAccountSummary'
import { PlatformBalance } from '@/src/experiences/card-payments/screens/MakePayment/Balance/PlatformBalance'
import type { BalanceComponentType } from '@/src/experiences/card-payments/screens/MakePayment/Balance'
import { LegacyBalance } from '@/src/experiences/card-payments/screens/MakePayment/Balance/LegacyBalance'
import { useNewestApplication } from '@/src/experiences/card-application/hooks/useNewestApplication'

export default function CardManagementHome() {
  const query = useNewestApplication()
  const refetchApplications = useEvent(() => void query.refetch())
  const Alert = useAlert(CardManagementHome.name)
  const logError = useLogError()

  useEffect(() => {
    if (
      !query.isUninitialized &&
      !query.isLoading &&
      !query.isFetching &&
      !query.data?.applications.length
    ) {
      logError(
        `Card account summary attempted to be accessed when no card application was found`,
        query,
      )
      Alert.alert(
        `Card information not found`,
        'Please try again later or contact support@mesamember.com',
        [
          { text: 'OK', onPress: () => void routerBack() },
          {
            text: 'Contact support',
            onPress: () => void Linking.openURL('mailto:support@mesamember.com'),
          },
        ],
      )
    }
  }, [query, Alert, logError])

  const newestApplication = query.data?.newestApplication

  if (!newestApplication) {
    return <CardAccountModule refetchCardAccountSummary={refetchApplications} />
  }

  switch (newestApplication.status) {
    case 'ISSUED':
      switch (newestApplication.platform) {
        case 'HIGHNOTE':
          return <LegacyBalance BalanceComponent={CardAccountModule} />
        default:
          return <Redirect href="/main/card-application/applicationApproved" />
      }
    case 'APPROVAL_ACKED':
      switch (newestApplication.platform) {
        case 'HIGHNOTE':
          return <LegacyBalance BalanceComponent={CardAccountModule} />
        default:
          return <PlatformBalance BalanceComponent={CardAccountModule} />
      }
    default:
      return <Redirect href="/main/card/cardBenefits" />
  }
}

const CardAccountModule = ((props) => {
  const bottomTabBarHeight = useBottomTabBarHeight()
  /** @todo stop this hardcoded nonsense */
  const tabBarHeight = Platform.OS === 'web' ? 88 : bottomTabBarHeight
  const cardActivityFeedBottomSheetHeightStore =
    useCardActivityFeedBottomSheetHeightStore(tabBarHeight)

  return (
    <CardAccountSummary
      refetchCardAccountSummary={props.refetchCardAccountSummary}
      cardActivityFeedBottomSheetHeightStore={cardActivityFeedBottomSheetHeightStore}
    >
      <CardManagementModule
        cardActivityFeedBottomSheetHeightStore={cardActivityFeedBottomSheetHeightStore}
        {...props}
      />
    </CardAccountSummary>
  )
}) satisfies BalanceComponentType
